/**
 * AIChatPage - Lions Magazine AI Assistant
 * Connected to Lions chatbot API — with per-user session management
 * Chat history is persisted to the backend (account-level) so it survives re-logins.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container, Box, Typography, TextField, IconButton,
  Paper, CircularProgress, Avatar, Button,
  List, ListItemButton, ListItemText, ListItemSecondaryAction,
  Divider, Chip, Tooltip, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  AddComment as NewChatIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDown as ThumbDownIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { colors } from '../theme';
import { authAPI, chatSessionAPI, chatbotAPI } from '../services/api';

// ── session helpers ───────────────────────────────────────────────────────────

const generateSessionId = () =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const STORAGE_KEY_PREFIX = 'lions_chat_sessions_';
const getStorageKey = (lciId) => `${STORAGE_KEY_PREFIX}${lciId || 'anonymous'}`;

const loadSessions = (lciId) => {
  try { return JSON.parse(localStorage.getItem(getStorageKey(lciId)) || '{}'); }
  catch { return {}; }
};

const saveSessions = (lciId, sessions) => {
  try { localStorage.setItem(getStorageKey(lciId), JSON.stringify(sessions)); }
  catch {}
};

// Accept both Unix timestamp (ms) and ISO string from backend
const fmtDate = (ts) => {
  const d = typeof ts === 'string' ? new Date(ts) : new Date(ts);
  return d.toLocaleString('zh-HK', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── text helpers ──────────────────────────────────────────────────────────────

const SUGGESTION_HEADING_RE = /^\*\*您可以繼續問[：:]\*\*\s*$/;
const PAGE_LINK_RE = /magazine\.lionsclubs\.org\.hk\/pages\//i;
const NUMBERED_LINE_RE = /^\d+[.)、]\s+(.+)$/;

const LOADING_STATUS = {
  checking: { zh: '正在檢查問題…', en: 'Checking your question…' },
  routing: { zh: '正在理解你的問題…', en: 'Understanding your question…' },
  searching: { zh: '正在搜尋《港澳獅聲》雜誌…', en: 'Searching Lions Magazine…' },
  reranking: { zh: '正在挑選最相關的內容…', en: 'Selecting the most relevant articles…' },
  answering: { zh: '正在撰寫回覆…', en: 'Writing a reply…' },
  translating: { zh: '正在翻譯回覆…', en: 'Translating the reply…' },
  cached: { zh: '正在載入先前的結果…', en: 'Loading a previous result…' },
  followups: { zh: '正在準備相關問題…', en: 'Preparing related questions…' },
};

const statusLabel = (data, lang) => {
  const mapped = LOADING_STATUS[data?.stage];
  if (mapped) return mapped[lang] || mapped.zh;
  return data?.message || (lang === 'zh' ? '處理中…' : 'Working…');
};

// Tighten model output: join split titles, drop heading markup, collapse blank lines.
const normalizeBotMarkdown = (text) => {
  if (!text) return text;
  let s = String(text).replace(/\r\n/g, '\n');

  // Join **bold** titles that were split across a newline.
  s = s.replace(/\*\*([^*\n]+)\n+([^*\n]+)\*\*/g, '**$1$2**');

  // Join ATX headings split onto the next line: "## 醫療同\n健康機構"
  s = s.replace(
    /^(#{1,6}[ \t]*)([^\n]+)\n(?!#{1,6}|\s*[-*•]|\s*\d+[.)])([^\n]{1,24})$/gm,
    (_, hashes, a, b) => `${hashes}${a.replace(/\s+$/, '')}${b.trimStart()}`,
  );

  // Join a short title fragment with the next line ending in a colon.
  s = s.replace(
    /^([^\n#*。！？.!?：:]{2,16})\n([^\n#*]{1,20}[：:])\s*$/gm,
    '$1$2',
  );

  // Render # headings as bold single-line titles (chat bubbles can't host large h1/h2).
  s = s.replace(/^#{1,6}[ \t]+(.+?)\s*#*\s*$/gm, '**$1**');

  // Bold short standalone category titles like "醫療同健康機構："
  s = s.replace(
    /^(?![-*#>\s]|\d)([^\n]{2,20}[：:])\s*$/gm,
    (m, title) => {
      if (title.startsWith('**') || /以下|這些|如下|包括/.test(title)) return m;
      return `**${title.replace(/[：:]\s*$/, '')}**`;
    },
  );

  // Turn "機構名：簡述" paragraphs into compact bullets.
  s = s.replace(
    /^(?![-*#>\s]|\d)([^\n。！？!?]{2,30})[：:](.+)$/gm,
    (m, name, desc) => {
      if (name.startsWith('**') || !desc.trim() || desc.trim().length < 6) return m;
      return `- **${name.trim()}**：${desc.trim()}`;
    },
  );

  s = s.replace(/\n{3,}/g, '\n\n');
  s = s.replace(/(^[-*][^\n]+)\n\n+(?=[-*])/gm, '$1\n');
  return s;
};

const isRecordLikeLine = (line) =>
  PAGE_LINK_RE.test(line) || /\[[^\]]*\]\(https?:\/\//i.test(line);

// Extract exactly 3 follow-up suggestions; keep record bullets in the body.
const parseAnswer = (text) => {
  const lines = text.split('\n');
  let headingIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (SUGGESTION_HEADING_RE.test(lines[i].trim())) {
      headingIdx = i;
      break;
    }
  }

  const collectNumbered = (startIdx) => {
    const out = [];
    for (let i = startIdx + 1; i < lines.length && out.length < 3; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed) continue;
      const m = trimmed.match(NUMBERED_LINE_RE);
      if (m && !isRecordLikeLine(trimmed)) out.push(m[1]);
      else if (out.length) break;
    }
    return out;
  };

  if (headingIdx >= 0) {
    return {
      body: lines.slice(0, headingIdx).join('\n').trimEnd(),
      suggestions: collectNumbered(headingIdx),
    };
  }

  // Fallback: last up to 3 numbered lines without page links (legacy answers).
  const suggestions = [];
  let cutIdx = lines.length;
  for (let i = lines.length - 1; i >= 0 && suggestions.length < 3; i--) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;
    const m = trimmed.match(NUMBERED_LINE_RE);
    if (m && !isRecordLikeLine(trimmed)) {
      suggestions.unshift(m[1]);
      cutIdx = i;
    } else break;
  }
  return {
    body: lines.slice(0, cutIdx).join('\n').trimEnd(),
    suggestions,
  };
};

const renderArchiveLink = (href, children) => {
  const isArchivePage = href && PAGE_LINK_RE.test(href);
  const label = String(children ?? '').trim();
  if (isArchivePage || label === '查看' || label === '🔗') {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" title="查看雜誌頁面"
        style={{ color: '#1976d2', textDecoration: 'none', marginLeft: '0.25em' }}>
        🔗
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{ color: '#1976d2', wordBreak: 'break-all' }}>
      {children}
    </a>
  );
};

const formatCitationLabel = (c) => {
  const parts = [];
  if (c.issue_no != null) parts.push(`第${c.issue_no}期`);
  if (c.issue_year) {
    let y = `${c.issue_year}年`;
    if (c.issue_month) y += `${c.issue_month}月`;
    parts.push(y);
  }
  if (c.club) parts.push(c.club);
  return parts.join(' · ') || `來源 ${c.index}`;
};

const citationSourceKey = (c) =>
  `${c.issue_no ?? ''}|${c.issue_year ?? ''}|${c.issue_month ?? ''}|${c.club ?? ''}`;

const distinctCitations = (citations) => {
  const byKey = new Map();
  for (const c of citations) {
    const key = citationSourceKey(c);
    const existing = byKey.get(key);
    if (!existing || (c.index ?? 999) < (existing.index ?? 999)) {
      byKey.set(key, c);
    }
  }
  return [...byKey.values()].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
};

const sectionTitleStyle = {
  margin: '0.65em 0 0.25em',
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1.45,
};

const LoadingStatus = ({ text }) => (
  <Box
    aria-live="polite"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: 'fit-content',
    }}
  >
    <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: colors.royalBlue }} />
    <Typography
      component="span"
      sx={{
        fontSize: '0.88rem',
        color: colors.royalBlue,
        fontStyle: 'italic',
        lineHeight: 1.45,
        minWidth: '8em',
      }}
    >
      {text}
    </Typography>
  </Box>
);

// Render bot response text as markdown (bold, lists, links, etc.)
const BotMessage = ({ text }) => (
  <Box sx={{ '& > *:last-child': { marginBottom: '0 !important' } }}>
    <ReactMarkdown
      components={{
        a: ({ node, href, children, ...props }) => renderArchiveLink(href, children),
        p: ({ node, ...props }) => <p {...props} style={{ margin: '0 0 0.55em' }} />,
        ul: ({ node, ...props }) => <ul {...props} style={{ margin: '0.15em 0 0.55em', paddingLeft: '1.25em' }} />,
        ol: ({ node, ...props }) => <ol {...props} style={{ margin: '0.15em 0 0.55em', paddingLeft: '1.25em' }} />,
        li: ({ node, ...props }) => <li {...props} style={{ marginBottom: '0.28em' }} />,
        h1: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
        h2: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
        h3: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
        h4: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
        h5: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
        h6: ({ node, ...props }) => <p {...props} style={sectionTitleStyle} />,
      }}
    >
      {normalizeBotMarkdown(text)}
    </ReactMarkdown>
  </Box>
);

const CitationsList = ({ citations, lang }) => {
  const unique = distinctCitations(citations ?? []);
  if (!unique.length) return null;
  return (
    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {lang === 'zh' ? '參考來源' : 'References'}
      </Typography>
      {unique.map((c) => (
        <Box key={c.index ?? c.chunk_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35, fontSize: '0.82rem', lineHeight: 1.4 }}>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            [{c.index}]
          </Typography>
          <Typography component="span" variant="caption" color="text.primary">
            {formatCitationLabel(c)}
          </Typography>
          {c.page_url && renderArchiveLink(c.page_url, '查看')}
        </Box>
      ))}
    </Box>
  );
};

const MessageActions = ({ feedback, lang, onCopy, onFeedback }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pl: 5, mt: 0.25 }}>
    <Tooltip title={lang === 'zh' ? '複製' : 'Copy'}>
      <IconButton size="small" onClick={onCopy} sx={{ color: 'text.secondary' }}>
        <CopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
    <Tooltip title={lang === 'zh' ? '有用' : 'Helpful'}>
      <IconButton
        size="small"
        onClick={() => onFeedback(feedback === 'up' ? null : 'up')}
        sx={{ color: feedback === 'up' ? colors.royalBlue : 'text.secondary' }}
      >
        {feedback === 'up' ? <ThumbUpIcon sx={{ fontSize: 16 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
    <Tooltip title={lang === 'zh' ? '無用' : 'Not helpful'}>
      <IconButton
        size="small"
        onClick={() => onFeedback(feedback === 'down' ? null : 'down')}
        sx={{ color: feedback === 'down' ? 'error.main' : 'text.secondary' }}
      >
        {feedback === 'down' ? <ThumbDownIcon sx={{ fontSize: 16 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  </Box>
);

// ── component ─────────────────────────────────────────────────────────────────

const HISTORY_WIDTH = 260;

const AIChatPage = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const user = authAPI.getUser();
  const lciId = user?.username || user?.lci_id || 'anonymous';

  const welcomeText = () =>
    i18n.language === 'zh'
      ? '你好！我是港澳獅聲 AI 助手。請問有什麼可以幫你？'
      : 'Hello! I am the Lions Magazine AI Assistant. How can I help you?';

  const isAuthenticated = authAPI.isAuthenticated();

  // session state
  const [sessions, setSessions] = useState(() => loadSessions(lciId));
  const [activeSessionId, setActiveSessionId] = useState(null); // null = no session yet
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  // backendMap: { [session_id]: backendPk } — maps local session IDs to backend primary keys
  const backendMapRef = useRef({});

  // chat state — starts with just the welcome message, no session
  const [messages, setMessages] = useState([{ role: 'assistant', text: welcomeText() }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [copyTip, setCopyTip] = useState('');
  const messagesBoxRef = useRef(null);
  const inputRef = useRef(null);
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  const scrollToBottom = () => {
    if (messagesBoxRef.current)
      messagesBoxRef.current.scrollTop = messagesBoxRef.current.scrollHeight;
  };

  // ── Backend sync on mount ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const syncFromBackend = async () => {
      try {
        const res = await chatSessionAPI.getAll();
        const serverSessions = res.data?.results ?? res.data ?? [];

        if (serverSessions.length > 0) {
          // Build sessions map from server data (server is authoritative)
          const fromServer = {};
          serverSessions.forEach((s) => {
            fromServer[s.session_id] = {
              id: s.session_id,
              backendId: s.id,
              createdAt: new Date(s.created_at).getTime(),
              updatedAt: new Date(s.updated_at).getTime(),
              messages: s.messages || [],
              preview: s.preview || '對話',
            };
            backendMapRef.current[s.session_id] = s.id;
          });
          setSessions(fromServer);
          saveSessions(lciId, fromServer);
        } else {
          // No server sessions yet — migrate localStorage ones to backend
          const local = loadSessions(lciId);
          const localList = Object.values(local);
          if (localList.length > 0) {
            const payload = localList.map((s) => ({
              session_id: s.id,
              preview: s.preview || '對話',
              messages: s.messages || [],
            }));
            await chatSessionAPI.bulkSync(payload);
            // Re-fetch to get the backend PKs
            const res2 = await chatSessionAPI.getAll();
            const synced = res2.data?.results ?? res2.data ?? [];
            synced.forEach((s) => { backendMapRef.current[s.session_id] = s.id; });
          }
        }
      } catch (err) {
        // Backend unavailable — silently fall back to localStorage
        console.warn('Chat history sync failed, using localStorage fallback', err);
      }
    };

    syncFromBackend();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Persist messages ──────────────────────────────────────────────────────
  const persistMessages = useCallback((msgs, sid, base, extra = {}) => {
    const sessionId = sid || activeSessionId;
    const current = base || sessions;
    if (!sessionId || !current[sessionId]) return current;
    const preview = msgs.find((m) => m.role === 'user')?.text?.slice(0, 40) || '對話';
    const updated = {
      ...current,
      [sessionId]: {
        ...current[sessionId],
        messages: msgs,
        updatedAt: Date.now(),
        preview,
        ...extra,
      },
    };
    setSessions(updated);
    saveSessions(lciId, updated);

    // Sync to backend
    if (isAuthenticated) {
      const backendId = backendMapRef.current[sessionId];
      if (backendId) {
        chatSessionAPI.update(backendId, { messages: msgs, preview }).catch(() => {});
      }
    }

    return updated;
  }, [activeSessionId, sessions, lciId, isAuthenticated]);

  // ── Create a new session ──────────────────────────────────────────────────
  const createSession = useCallback((base) => {
    const current = base !== undefined ? base : sessions;
    const id = generateSessionId();
    const welcome = { role: 'assistant', text: welcomeText() };
    const newSession = { id, createdAt: Date.now(), updatedAt: Date.now(), messages: [welcome], preview: '新對話' };
    const updated = { ...current, [id]: newSession };
    setSessions(updated);
    saveSessions(lciId, updated);

    // Persist to backend
    if (isAuthenticated) {
      chatSessionAPI.create({ session_id: id, preview: '新對話', messages: [welcome] })
        .then((res) => { backendMapRef.current[id] = res.data.id; })
        .catch(() => {});
    }

    return { id, updated };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, lciId, i18n.language, isAuthenticated]);

  // "New Chat" — reset UI to welcome screen without creating a session
  const newChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([{ role: 'assistant', text: welcomeText() }]);
    setMobileHistoryOpen(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // load an existing session from history
  const loadSession = (id) => {
    const s = sessions[id];
    if (!s) return;
    setActiveSessionId(id);
    setMessages(s.messages?.length ? s.messages : [{ role: 'assistant', text: welcomeText() }]);
    setMobileHistoryOpen(false);
  };

  // delete a session
  const deleteSession = (e, id) => {
    e.stopPropagation();
    const updated = { ...sessions };
    delete updated[id];
    setSessions(updated);
    saveSessions(lciId, updated);
    if (activeSessionId === id) newChat();

    // Remove from backend
    if (isAuthenticated) {
      const backendId = backendMapRef.current[id];
      if (backendId) {
        chatSessionAPI.delete(backendId).catch(() => {});
        delete backendMapRef.current[id];
      }
    }
  };

  useLayoutEffect(() => {
    scrollToBottom();
  }, [messages, loading, loadingStatus]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // Create session on first message if none exists
    let sid = activeSessionId;
    let currentSessions = sessions;
    if (!sid) {
      const { id, updated } = createSession();
      sid = id;
      currentSessions = updated;
      setActiveSessionId(id);
    }

    const next = [...messages, { role: 'user', text }];
    setMessages(next);
    persistMessages(next, sid, currentSessions);
    setInput('');
    setLoading(true);
    setLoadingStatus(lang === 'zh' ? '正在檢查問題…' : 'Checking your question…');

    const history = messages
      .filter((m) => m.text && (m.role === 'user' || m.role === 'assistant'))
      .map((m) => ({ role: m.role, content: m.text }));

    const chatbotSid = currentSessions[sid]?.chatbotSessionId ?? sid;
    const assistantIdx = next.length;
    const withPlaceholder = [...next, { role: 'assistant', text: '' }];
    setMessages(withPlaceholder);

    try {
      let streamed = '';
      const data = await chatbotAPI.streamChat({
        sessionId: chatbotSid,
        message: text,
        history,
        onStatus: (status) => {
          setLoadingStatus(statusLabel(status, lang));
        },
        onToken: (chunk) => {
          setLoadingStatus('');
          streamed += chunk;
          setMessages((prev) => {
            const updated = [...prev];
            if (updated[assistantIdx]) {
              updated[assistantIdx] = { role: 'assistant', text: streamed };
            }
            return updated;
          });
        },
      });

      setLoadingStatus('');
      const answer = data.answer || streamed;
      const { body, suggestions: parsed } = parseAnswer(answer);
      const followups = (data.followups?.length ? data.followups : parsed).slice(0, 3);

      const newMsgs = [];
      const displayText = body || answer;
      if (displayText) {
        newMsgs.push({
          role: 'assistant',
          text: displayText,
          citations: data.citations?.length ? data.citations : undefined,
        });
      }
      if (followups.length) {
        newMsgs.push({ role: 'assistant', suggestions: followups });
      }
      if (!newMsgs.length) newMsgs.push({ role: 'assistant', text: answer });

      const withAi = [...next, ...newMsgs];
      setMessages(withAi);
      persistMessages(withAi, sid, currentSessions);
    } catch {
      setLoadingStatus('');
      const errMsg = { role: 'assistant', text: i18n.language === 'zh' ? '抱歉，發生錯誤，請稍後再試。' : 'Sorry, an error occurred. Please try again.' };
      const withErr = [...next, errMsg];
      setMessages(withErr);
      persistMessages(withErr, sid, currentSessions);
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleSuggestionClick = (text) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip(lang === 'zh' ? '已複製' : 'Copied');
      setTimeout(() => setCopyTip(''), 2000);
    } catch {
      setCopyTip(lang === 'zh' ? '複製失敗' : 'Copy failed');
      setTimeout(() => setCopyTip(''), 2000);
    }
  };

  const handleMessageFeedback = (idx, feedback) => {
    setMessages((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], feedback };
      persistMessages(updated);
      return updated;
    });
  };

  const sortedSessions = Object.values(sessions).sort((a, b) => b.updatedAt - a.updatedAt);
  const lastMsg = messages[messages.length - 1];
  const lastHasAssistantText = lastMsg?.role === 'assistant' && !!lastMsg.text?.trim();
  const waitingForFirstToken = loading && !lastHasAssistantText;

  // ── History panel (shared content) ────────────────────────────────────────
  const historyPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {i18n.language === 'zh' ? '對話記錄' : 'Chat History'}
        </Typography>
        {lciId !== 'anonymous' && (
          <Typography variant="caption" color="text.secondary">LCI: {lciId}</Typography>
        )}
      </Box>
      <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
        {sortedSessions.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {i18n.language === 'zh' ? '暫無記錄' : 'No history yet'}
            </Typography>
          </Box>
        )}
        {sortedSessions.map((s, idx) => (
          <React.Fragment key={s.id}>
            {idx > 0 && <Divider />}
            <ListItemButton
              selected={s.id === activeSessionId}
              onClick={() => loadSession(s.id)}
              sx={{ py: 1, pr: 5, '&.Mui-selected': { bgcolor: `${colors.royalBlue}12` } }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={s.id === activeSessionId ? 700 : 400}
                    sx={{ color: s.id === activeSessionId ? colors.royalBlue : 'text.primary' }} noWrap>
                    {s.preview || '對話'}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">{fmtDate(s.updatedAt)}</Typography>
                }
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" size="small" onClick={(e) => deleteSession(e, s.id)}
                  sx={{ '&:hover': { color: 'error.main' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ position: 'relative', backgroundColor: colors.lightGray, minHeight: 'calc(100vh - 64px)' }}>
      {/* Faint bg watermark */}
      <Box sx={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'url(/hero-bg.jpeg)', backgroundSize: 'cover',
        backgroundPosition: 'center', opacity: 0.06,
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 3 }}>
        {/* Hero banner */}
        <Box sx={{
          position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3,
          backgroundImage: 'url(/hero-bg.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center 30%',
          height: { xs: 110, sm: 140 },
        }}>
          <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${colors.royalBlue}CC 0%, #1565C0BB 100%)` }} />
          <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,0.9)', border: '2px solid rgba(255,255,255,0.6)', backdropFilter: 'blur(4px)', p: 0.5 }}>
              <img src="/Lions_Clubs_International_logo.svg" alt="Lions" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                  {i18n.language === 'zh' ? 'AI 助手' : 'AI Assistant'}
                </Typography>
                <Tooltip title={i18n.language === 'zh' ? '此功能仍在測試中' : 'This feature is still in beta'}>
                  <Chip
                    label={i18n.language === 'zh' ? '測試版' : 'Beta'}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(255,255,255,0.92)',
                      color: colors.royalBlue,
                      letterSpacing: '0.04em',
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25 }}>
                {i18n.language === 'zh' ? '智能搜尋期刊內容、活動資訊' : 'Intelligent search for magazine content'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main layout: sidebar (desktop) + chat */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

          {/* ── History sidebar (desktop) ── */}
          {isDesktop && (
            <Paper elevation={1} sx={{
              width: HISTORY_WIDTH, flexShrink: 0,
              borderRadius: 2, overflow: 'hidden',
              height: { md: 'calc(60vh + 48px)' }, display: 'flex', flexDirection: 'column',
            }}>
              {historyPanel}
            </Paper>
          )}

          {/* ── Chat column ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Top bar: New Chat + mobile History toggle */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<NewChatIcon />} variant="outlined" size="small" onClick={newChat}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
                {i18n.language === 'zh' ? '新對話' : 'New Chat'}
              </Button>
              {!isDesktop && (
                <Button
                  startIcon={mobileHistoryOpen ? <CloseIcon /> : <HistoryIcon />}
                  variant="outlined" size="small"
                  onClick={() => setMobileHistoryOpen((v) => !v)}
                  sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
                  {i18n.language === 'zh' ? '記錄' : 'History'}
                </Button>
              )}
            </Box>

            {/* Mobile history panel (inline, collapsible above chat) */}
            {!isDesktop && mobileHistoryOpen && (
              <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', maxHeight: 280 }}>
                {historyPanel}
              </Paper>
            )}

            {/* Chat window */}
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: { xs: '60vh', md: '60vh' }, maxHeight: 700 }}>
              <Box ref={messagesBoxRef} sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, backgroundColor: '#f9f9f9' }}>
                {messages.map((msg, idx) => {
                  const isUser = msg.role === 'user';
                  const text = msg.text?.trim() ?? '';
                  const isLast = idx === messages.length - 1;
                  const showStatusInBubble = loading && isLast && !isUser && !msg.suggestions && !!text;

                  // Empty streaming placeholder is rendered as the dedicated loading row below.
                  if (!isUser && !msg.suggestions && !text) return null;

                  if (msg.suggestions) {
                    return (
                      <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 5 }}>
                        {msg.suggestions.map((s, si) => (
                          <Button key={si} variant="outlined" size="small" onClick={() => handleSuggestionClick(s)}
                            sx={{ justifyContent: 'flex-start', textAlign: 'left', borderColor: colors.lionsGold, color: 'text.primary', backgroundColor: 'rgba(253,176,39,0.08)', borderRadius: 3, textTransform: 'none', fontSize: '0.88rem', lineHeight: 1.5, '&:hover': { backgroundColor: 'rgba(253,176,39,0.2)', borderColor: colors.lionsGold } }}>
                            {s}
                          </Button>
                        ))}
                      </Box>
                    );
                  }

                  return (
                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 1, minWidth: 0 }}>
                        {!isUser && (
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', flexShrink: 0, border: `1px solid ${colors.royalBlue}30`, p: 0.4 }}>
                            <img src="/Lions_Clubs_International_logo.svg" alt="Lions" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Avatar>
                        )}
                        <Box sx={{ maxWidth: { xs: '82%', md: '75%' }, minWidth: 0, px: 2, py: 1.25, borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px', backgroundColor: isUser ? colors.royalBlue : 'white', color: isUser ? 'white' : 'text.primary', boxShadow: 1, whiteSpace: isUser ? 'pre-wrap' : 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', overflow: 'hidden', fontSize: '0.92rem', lineHeight: 1.6 }}>
                          {isUser ? msg.text : (
                            <>
                              {showStatusInBubble && (
                                <Box sx={{ mb: text ? 1 : 0 }}>
                                  <LoadingStatus text={loadingStatus || (lang === 'zh' ? '處理中…' : 'Working…')} />
                                </Box>
                              )}
                              <BotMessage text={msg.text} />
                              <CitationsList citations={msg.citations} lang={lang} />
                            </>
                          )}
                        </Box>
                        {isUser && (
                          <Avatar sx={{ width: 32, height: 32, bgcolor: colors.lionsGold, flexShrink: 0 }}>
                            <PersonIcon sx={{ fontSize: 18, color: colors.royalBlue }} />
                          </Avatar>
                        )}
                      </Box>
                      {!isUser && text && !loading && (
                        <MessageActions
                          feedback={msg.feedback}
                          lang={lang}
                          onCopy={() => handleCopy(msg.text)}
                          onFeedback={(fb) => handleMessageFeedback(idx, fb)}
                        />
                      )}
                    </Box>
                  );
                })}
                {waitingForFirstToken && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', flexShrink: 0, border: `1px solid ${colors.royalBlue}30`, p: 0.4 }}>
                      <img src="/Lions_Clubs_International_logo.svg" alt="Lions" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Avatar>
                    <Box sx={{
                      flex: '0 0 auto',
                      px: 2,
                      py: 1.25,
                      borderRadius: '18px 18px 18px 4px',
                      backgroundColor: 'white',
                      boxShadow: 1,
                      maxWidth: { xs: '82%', md: '75%' },
                    }}>
                      <LoadingStatus text={loadingStatus || (lang === 'zh' ? '處理中…' : 'Working…')} />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'white', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth multiline maxRows={4} size="small"
                  placeholder={i18n.language === 'zh' ? '輸入訊息...' : 'Type a message...'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />
                <IconButton onClick={sendMessage} disabled={!input.trim() || loading}
                  sx={{ backgroundColor: colors.royalBlue, color: 'white', borderRadius: 2, width: 40, height: 40, flexShrink: 0, '&:hover': { backgroundColor: '#1565C0' }, '&.Mui-disabled': { backgroundColor: '#ccc', color: 'white' } }}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>

      {copyTip && (
        <Box sx={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          px: 2, py: 0.75, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.75)', color: 'white',
          fontSize: '0.85rem', zIndex: 1400,
        }}>
          {copyTip}
        </Box>
      )}
    </Box>
  );
};

export default AIChatPage;
